
import { useEffect, useRef, useState } from 'react';

interface FadeInOptions {
  threshold?: number;
  delay?: number;
  direction?: 'up' | 'left' | 'right' | 'none';
}

export function useFadeIn(options: FadeInOptions = {}) {
  const { threshold = 0.15, delay = 0, direction = 'up' } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (delay > 0) {
            setTimeout(() => setIsVisible(true), delay);
          } else {
            setIsVisible(true);
          }
          observer.unobserve(el);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, delay]);

  const getTransform = () => {
    if (isVisible) return 'translate(0, 0)';
    switch (direction) {
      case 'up': return 'translateY(40px)';
      case 'left': return 'translateX(-60px)';
      case 'right': return 'translateX(60px)';
      case 'none': return 'none';
      default: return 'translateY(40px)';
    }
  };

  const style: React.CSSProperties = {
    opacity: isVisible ? 1 : 0,
    transform: getTransform(),
    transition: 'opacity 2s ease-out, transform 2s ease-out',
  };

  return { ref, style, isVisible };
}
